const SPIN_EASE = 0.09;
const SPIN_SETTLED = 0.0004;
const MAX_LEAN = (18 * Math.PI) / 180;
const TUMBLE_WOBBLE = (14 * Math.PI) / 180;
const LEAN_PHASE = 0.9;
const LUNGE_DEPTH = 0.30;
const LUNGE_CYCLES = 1.5;
const LUNGE_DAMPING = 0.6;
const FLOAT_PERIOD = 5200;
const FLOAT_SWAY_PERIOD = 7300;
const FLOAT_RISE = 0.022;
const FLOAT_SWAY = 0.018;
const FLOAT_TURN = (2.4 * Math.PI) / 180;
const FLOAT_PITCH_PERIOD = 6100;
const FLOAT_PITCH = (3.5 * Math.PI) / 180;
const FLOAT_HOLD = 0.06;
const TUMBLE_LEAD = 1.6;
const LANDING_SHRINK_START = 0.55;
const LANDING_RIPPLE = 0.1;
const MOBILE_PICKUP = 0.72;

class ComponentScrollStory extends Theme.Component {
  #spin = 0;
  #spin_frame = null;
  #float_frame = null;
  #float_base = null;
  #float_base_x = 0;
  #float_span = 0;
  #lunge = 0;
  #three = null;
  #renderer = null;
  #scene = null;
  #camera = null;
  #model = null;
  #frame = null;
  #progress = 0;
  #started = false;
  #track_home = null;
  #track_centred = false;
  #can_height = 0;
  #can_share = null;
  #blend = 0;
  #blend_from = 0;
  #blend_target = 0;
  #blend_at = 0;
  #blend_tick = null;
  #last_scroll = 0;
  #upward = false;
  #pin_to_card = false;
  #start_home = null;
  #release_at = null;
  #release_from = 0;
  #reason = -1;
  #loading = false;
  #page_ready = false;
  #observer = null;
  #track_observer = null;
  #panel_observer = null;
  #settled = false;
  #docked = null;
  #landing_shift = null;

  connectedCallback() {
    this.defineElements({
      visual: ":scope > .product-scroll-story-visual",
      canvas: "[data-element='canvas']",
      panels: "[data-element='panels']",
      start_media: "[data-element='start-media']",
      why: "[data-product-scroll-story-panel='why-recess']",
      slider: "[data-product-scroll-story-panel='collections-slider']"
    });

    this.defineElementCollections({
      reasons: "[data-elements='reasons']"
    });

    this.addActionEvents();

    this.root = this.closest(".product-scroll-story");
    this.landing_card = this.findLandingCard();

    this.setReason(0);
    this.measure();

    this.addDirectEvent("scroll", window, this.request.bind(this));
    this.addDirectEvent("resize", window, this.onResize.bind(this));

    const track = this.track();
    const scroller = this.landing_card?.closest("component-scroll");

    if (track) {
      this.addDirectEvent("scroll", track, this.request.bind(this));

      this.#track_observer = new ResizeObserver(() => {
        if (this.#progress >= 1 || this.#blend > 0) {
          return;
        }

        this.initTrack();
      });

      this.#track_observer.observe(track);
      Array.from(track.children).forEach((child) => this.#track_observer.observe(child));
    }

    if (scroller) {
      ["pointerdown", "wheel", "touchstart", "keydown"].forEach((type) => {
        this.addDirectEvent(type, scroller, this.forceLanding.bind(this));
      });
    }

    const start_scroller = this.startScroller();

    if (start_scroller && start_scroller !== scroller) {
      const start_track = this.startTrack();

      if (start_track) {
        this.addDirectEvent("scroll", start_track, this.request.bind(this));
      }

      ["pointerdown", "wheel", "touchstart", "keydown"].forEach((type) => {
        this.addDirectEvent(type, start_scroller, this.forceLanding.bind(this));
      });
    }

    const panel = this.landingPanel();

    if (panel) {
      this.#panel_observer = new MutationObserver(() => this.measure());
      this.#panel_observer.observe(panel, { attributes: true, attributeFilter: ["hidden"] });
    }

    this.initTrack();

    if (!this.animatable()) {
      this.settle();
    }

    this.whenIdle(() => {
      this.#page_ready = true;
      this.considerLoading();
    });

    this.scheduleTrack();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#blend_tick);
    cancelAnimationFrame(this.#spin_frame);
    this.stopFloat();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#track_observer?.disconnect();
    this.#track_observer = null;
    this.#panel_observer?.disconnect();
    this.#panel_observer = null;
    this.#renderer?.dispose();
    this.#renderer = null;
  }

  animatable() {
    if (!this.dataset.modelUrl) {
      return false;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return false;
    }

    if (this.constrainedNetwork()) {
      return false;
    }

    if (window.matchMedia("(max-width: 767px)").matches) {
      return this.root?.dataset.mobileAnimation === "true";
    }

    return true;
  }

  findLandingCard() {
    const group = this.dataset.landingGroup;
    const id = this.dataset.landingProduct;

    const selectors = [
      group ? `.product-card[data-product-group="${group}"]` : null,
      id ? `.product-card[data-product-id="${id}"]` : null
    ].filter(Boolean);

    for (const selector of selectors) {
      const cards = Array.from(this.querySelectorAll(selector));
      const card = cards.find((element) => element.closest("[hidden]") == null) ?? cards[0];

      if (card) {
        return card;
      }
    }

    return null;
  }

  request() {
    if (this.#frame) {
      return;
    }

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.measure();
    });
  }

  measure() {
    const panels = this.elements.panels;
    const visual = this.elements.visual;

    if (!panels || !visual) {
      return;
    }

    this.#upward = window.scrollY < this.#last_scroll;

    if (this.#upward) {
      this.restoreTab();
    }

    this.releasePin();

    this.#progress = this.travelProgress();
    this.#last_scroll = window.scrollY;

    this.considerLoading();

    const live = this.#renderer != null && this.#model != null && this.landingVisible();

    this.setLive(live);
    this.anchorVisual();
    this.setReason(this.reasonFor());

    this.updateTrack();

    this.chaseSpin();
    this.floatModel();
  }

  landingPanel() {
    return this.landing_card?.closest("[role='tabpanel']") ?? null;
  }

  landingVisible() {
    const panel = this.landingPanel();

    return panel ? !panel.hasAttribute("hidden") : true;
  }

  restoreTab() {
    const panel = this.landingPanel();

    if (!panel || !panel.hasAttribute("hidden")) {
      return;
    }

    const tabs = panel.closest("component-tabs");
    const trigger = tabs?.querySelector(`[aria-controls="${panel.id}"]`);

    if (tabs && trigger) {
      tabs.select(trigger);
    }
  }

  mediaOf(card) {
    return card?.querySelector(".product-card-media") ?? null;
  }

  setLive(value) {
    if (this.#started === value) {
      return;
    }

    this.#started = value;

    if (value) {
      this.floatModel();
    } else {
      this.stopFloat();
    }

    this.root?.setAttribute("data-ready", value ? "true" : "false");
    this.elements.start_media?.classList.toggle("state--handed-over", value);

    const media = this.mediaOf(this.landing_card);
    const background = this.dataset.landingBackground;

    if (media && background) {
      media.style.setProperty("--product-card-plate", value ? `url("${background}")` : "none");
      media.classList.toggle("state--plate", value);
    }
  }

  settle() {
    if (this.#settled) {
      return;
    }

    this.#settled = true;
    this.root?.setAttribute("data-settled", "true");
  }

  restPoint() {
    return window.innerHeight / 2;
  }

  phone() {
    return window.matchMedia("(max-width: 599px)").matches;
  }

  pickupPoint() {
    const rest = this.restPoint();

    return this.phone() ? Math.max(rest, window.innerHeight * MOBILE_PICKUP) : rest;
  }

  anchorPoints() {
    const start = this.rectOf(this.elements.start_media);
    const landing = this.rectOf(this.mediaOf(this.landing_card));
    const rest = this.restPoint();

    if (!start || !landing) {
      return null;
    }

    return {
      start,
      landing,
      lift: window.scrollY + start.top + start.height / 2 - this.pickupPoint(),
      land: window.scrollY + landing.top + landing.height / 2 - rest
    };
  }

  travelProgress() {
    const points = this.anchorPoints();

    if (!points || points.land <= points.lift) {
      return 0;
    }

    const natural = this.clamp((window.scrollY - points.lift) / (points.land - points.lift), 0, 1);

    if (!this.#pin_to_card && this.#upward && this.#blend > 0 && this.#release_at === null) {
      this.#release_at = this.#last_scroll;
      this.#release_from = this.#blend;
      this.#blend_from = 0;
      this.#blend_target = 0;
    }

    if (this.#release_at === null) {
      this.#blend = this.blendNow();
    } else {
      const travelled = this.#release_at - window.scrollY;

      this.#blend = this.#release_from * (1 - this.smoothstep(0, 240, travelled));

      if (this.#blend <= 0.001) {
        this.#blend = 0;
        this.#release_at = null;
      }
    }

    const pinned = this.#pin_to_card ? 0 : 1;

    return natural + ((pinned - natural) * this.#blend);
  }

  blendNow() {
    if (this.#blend_from === this.#blend_target) {
      return this.#blend_target;
    }

    const elapsed = (performance.now() - this.#blend_at) / 600;
    const time = this.clamp(elapsed, 0, 1);
    const eased = time < 0.5 ? 2 * time * time : 1 - (Math.pow((-2 * time) + 2, 2) / 2);

    if (time >= 1) {
      this.#blend_from = this.#blend_target;
    }

    return this.#blend_from + ((this.#blend_target - this.#blend_from) * eased);
  }

  setBlend(target) {
    if (this.#blend_target === target) {
      return;
    }

    this.#blend_from = this.#blend;
    this.#blend_target = target;
    this.#blend_at = performance.now();

    this.tick();
  }

  tick() {
    if (this.#blend_tick) {
      return;
    }

    const step = () => {
      this.#blend_tick = null;
      this.measure();

      if (this.#blend !== this.#blend_target) {
        this.#blend_tick = requestAnimationFrame(step);
      }
    };

    this.#blend_tick = requestAnimationFrame(step);
  }

  forceLanding(event) {
    if (this.#blend_target === 1 || this.#progress >= 1) {
      return;
    }

    const scroller = event?.currentTarget ?? null;
    const from_start = scroller != null && scroller === this.startScroller();
    const scrollable = from_start && this.scrollerScrollable(scroller);

    if (from_start && !scrollable) {
      return;
    }

    this.#release_at = null;
    this.#pin_to_card = scrollable;

    if (this.#pin_to_card && this.#start_home === null) {
      this.#start_home = this.startTrack()?.scrollLeft ?? null;
    }

    this.setBlend(1);
  }

  startScroller() {
    return this.elements.start_media?.closest("component-scroll") ?? null;
  }

  startTrack() {
    return this.startScroller()?.querySelector(":scope > .scroll-track") ?? null;
  }

  scrollerScrollable(scroller) {
    const track = scroller?.querySelector?.(":scope > .scroll-track");

    return track != null && track.scrollWidth > track.clientWidth + 1;
  }

  releasePin() {
    if (!this.#pin_to_card) {
      return;
    }

    if (this.#blend_target === 1 && window.scrollY !== this.#last_scroll) {
      this.setBlend(0);
      this.returnStartTrack();
    }

    if (this.#blend === 0 && this.#blend_target === 0) {
      this.#pin_to_card = false;
      this.#start_home = null;
    }
  }

  returnStartTrack() {
    const track = this.startTrack();

    if (!track || this.#start_home === null) {
      return;
    }

    track.scrollTo({
      left: this.#start_home,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    });
  }

  anchorVisual() {
    const visual = this.elements.visual;
    const canvas = this.elements.canvas;
    const points = this.anchorPoints();

    if (!visual || !canvas || !points) {
      return;
    }

    const home = visual.getBoundingClientRect();
    const rise = this.restPoint() - (home.top + home.height / 2);
    const centre = { x: home.left + home.width / 2, y: home.top + home.height / 2 + rise };
    const offsetTo = (rect) => ({
      x: (rect.left + rect.width / 2) - centre.x,
      y: (rect.top + rect.height / 2) - centre.y
    });

    const released = this.smoothstep(0, 0.12, this.#progress);
    const arriving = this.smoothstep(0.88, 1, this.#progress);
    const shrinking = this.smoothstep(LANDING_SHRINK_START, 1, this.#progress);
    const shift = this.landingShift();
    const start = offsetTo(points.start);
    const landing = offsetTo(points.landing);

    landing.x += points.landing.width * shift.x;
    landing.y += points.landing.height * shift.y;

    const x = (start.x * (1 - released)) + (landing.x * arriving);
    const y = (start.y * (1 - released)) + (landing.y * arriving);
    const settled = this.landingScale(points.landing);
    const scale = (1 + ((settled - 1) * shrinking)) * (1 + (this.lungeReach() * LANDING_RIPPLE));

    const target = this.dockTarget();

    if (target) {
      this.dock(target, canvas, visual, scale);
      return;
    }

    this.undock(canvas, visual);

    canvas.style.transform = `translate(${x.toFixed(2)}px, ${(y + rise).toFixed(2)}px) scale(${scale.toFixed(4)})`;
  }

  landingShift() {
    if (this.#landing_shift === null) {
      const style = getComputedStyle(this);

      this.#landing_shift = {
        x: parseFloat(style.getPropertyValue("--product-scroll-story-landing-shift-x")) || 0,
        y: parseFloat(style.getPropertyValue("--product-scroll-story-landing-shift-y")) || 0
      };
    }

    return this.#landing_shift;
  }

  dockTarget() {
    if (this.#progress >= 1 && this.landing_card && this.landingVisible()) {
      const media = this.mediaOf(this.landing_card);

      return media ? { host: this.landing_card, media, shift: this.landingShift() } : null;
    }

    const start_media = this.elements.start_media;

    if (this.#progress <= 0 && start_media) {
      return { host: start_media.parentElement, media: start_media, shift: { x: 0, y: 0 } };
    }

    return null;
  }

  dock(target, canvas, visual, scale) {
    const { host, media, shift } = target;

    if (this.#docked !== host) {
      this.undock(canvas, visual);
      host.classList.add("state--docked");
      host.append(canvas);
      this.#docked = host;
    }

    const host_box = host.getBoundingClientRect();
    const media_box = media.getBoundingClientRect();
    const width = visual.offsetWidth;
    const height = visual.offsetHeight;
    const centre_x = (media_box.width / 2) + (media_box.width * shift.x);
    const centre_y = (media_box.height / 2) + (media_box.height * shift.y);
    const left = (media_box.left - host_box.left) + centre_x - (width / 2);
    const top = (media_box.top - host_box.top) + centre_y - (height / 2);

    canvas.style.inset = "auto";
    canvas.style.left = `${left.toFixed(2)}px`;
    canvas.style.top = `${top.toFixed(2)}px`;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.style.transform = `scale(${scale.toFixed(4)})`;
  }

  undock(canvas, visual) {
    if (!this.#docked) {
      return;
    }

    this.#docked.classList.remove("state--docked");
    visual.prepend(canvas);

    ["inset", "left", "top", "width", "height"].forEach((property) => canvas.style.removeProperty(property));

    this.#docked = null;
  }

  lungeReach() {
    const spin = this.#spin;

    return Math.sin(Math.PI * 2 * LUNGE_CYCLES * spin) * Math.exp(-LUNGE_DAMPING * spin);
  }

  landingScale(landing) {
    if (!landing || !this.#can_height) {
      return 1;
    }

    if (this.#can_share === null) {
      this.#can_share = parseFloat(getComputedStyle(this).getPropertyValue("--product-scroll-story-can-share")) || 0.55;
    }

    const image = this.mediaOf(this.landing_card)?.querySelector("img");
    const art_height = Math.max(landing.height, landing.width * this.aspectOf(image));

    return (art_height * this.#can_share) / this.#can_height;
  }

  aspectOf(image) {
    const width = image?.naturalWidth || parseFloat(image?.getAttribute("width")) || 0;
    const height = image?.naturalHeight || parseFloat(image?.getAttribute("height")) || 0;

    return width > 0 ? height / width : 1;
  }

  photoHeight() {
    const image = this.elements.start_media?.querySelector("img");

    if (!image) {
      return null;
    }

    const box = image.getBoundingClientRect();

    return Math.min(box.height, box.width * this.aspectOf(image)) || null;
  }

  smootherstep(value) {
    const t = this.clamp(value, 0, 1);

    return t * t * t * ((t * ((t * 6) - 15)) + 10);
  }

  smoothstep(from, to, value) {
    const t = this.clamp((value - from) / (to - from), 0, 1);

    return t * t * (3 - (2 * t));
  }

  rectOf(element) {
    return element ? element.getBoundingClientRect() : null;
  }

  reasonFor() {
    const why = this.elements.why;
    const reasons = this.elements.reasons ?? [];

    if (!why || reasons.length === 0) {
      return this.#reason;
    }

    const box = why.getBoundingClientRect();
    const span = box.height + window.innerHeight;
    const passed = window.innerHeight - box.top;
    const position = this.smootherstep(this.clamp(passed / span, 0, 0.999));

    return Math.floor(this.clamp(position, 0, 0.999) * reasons.length);
  }

  setReason(index) {
    const reasons = this.elements.reasons ?? [];

    if (index === this.#reason || index < 0 || reasons.length === 0) {
      return;
    }

    this.#reason = index;

    reasons.forEach((reason, position) => {
      const active = position === index;

      reason.classList.toggle("state--active", active);
      reason.querySelector("[data-action]")?.setAttribute("aria-expanded", active);
    });
  }

  scrollToReason(detail, element) {
    const reasons = this.elements.reasons ?? [];
    const why = this.elements.why;
    const index = reasons.indexOf(element.closest("[data-elements='reasons']"));

    if (!why || index === -1) {
      return;
    }

    const box = why.getBoundingClientRect();
    const span = box.height + window.innerHeight;
    const target = (index + 0.5) / reasons.length;
    const offset = window.scrollY + box.top - window.innerHeight + (target * span);

    window.scrollTo({
      left: 0,
      top: offset,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    });
  }

  track() {
    const scroller = this.landing_card?.closest("component-scroll");

    return scroller?.querySelector(":scope > .scroll-track") ?? null;
  }

  scheduleTrack() {
    const attempt = () => {
      if (this.#progress >= 1 || this.#blend > 0) {
        return;
      }

      this.initTrack();
    };

    requestAnimationFrame(() => requestAnimationFrame(attempt));

    [100, 300, 800, 1600].forEach((delay) => setTimeout(attempt, delay));

    if (document.readyState !== "complete") {
      this.addDirectEvent("load", window, attempt);
    }
  }

  driftedFromCard() {
    const visual = this.elements.visual;
    const card = this.landing_card;

    if (!visual || !card) {
      return false;
    }

    const box = card.getBoundingClientRect();
    const home = visual.getBoundingClientRect();

    return Math.abs((box.left + (box.width / 2)) - (home.left + (home.width / 2))) > 1;
  }

  initTrack() {
    const track = this.track();
    const visual = this.elements.visual;

    if (!track || !visual || !this.landing_card) {
      return;
    }

    const home = visual.getBoundingClientRect();
    const target = home.left + (home.width / 2);

    for (let pass = 0; pass < 3; pass++) {
      const card = this.landing_card.getBoundingClientRect();
      const drift = (card.left + (card.width / 2)) - target;

      if (Math.abs(drift) < 0.5) {
        break;
      }

      track.scrollLeft = track.scrollLeft + drift;
    }

    this.#track_home = track.scrollLeft;

    this.landing_card.closest("component-scroll")?.pinPosition(this.#track_home);

    this.request();
  }

  updateTrack() {
    const track = this.track();

    if (!track || this.#track_home === null) {
      return;
    }

    const landed = this.#progress >= 1;
    const idle = this.#blend === 0 && this.#release_at === null;

    if (!landed && idle && this.landingVisible() && this.driftedFromCard()) {
      this.initTrack();
    }

    if (this.#track_centred && !landed) {
      track.scrollTo({
        left: this.#track_home,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
      });
    }

    this.#track_centred = landed;
  }

  onResize() {
    this.#track_centred = false;
    this.#can_share = null;
    this.#landing_shift = null;
    this.resize();
    this.initTrack();
  }

  constrainedNetwork() {
    const connection = navigator.connection;

    if (!connection) {
      return false;
    }

    return connection.saveData === true || ["slow-2g", "2g", "3g"].includes(connection.effectiveType);
  }

  whenIdle(callback) {
    const idle = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback, { timeout: 2000 });
      } else {
        setTimeout(callback, 200);
      }
    };

    if (document.readyState === "complete") {
      idle();
    } else {
      window.addEventListener("load", idle, { once: true });
    }
  }

  considerLoading() {
    if (this.#loading || !this.#page_ready || !this.animatable()) {
      return;
    }

    const box = this.getBoundingClientRect();

    if (box.top > window.innerHeight || box.bottom < 0) {
      return;
    }

    this.load();
  }

  async load() {
    if (this.#loading) {
      return;
    }

    this.#loading = true;

    try {
      const three = await import(this.dataset.threeUrl);
      const { GLTFLoader } = await import(this.dataset.loaderUrl);
      const { MeshoptDecoder } = await import(this.dataset.meshoptUrl);

      this.#three = three;
      this.build(three);

      const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
      const gltf = await loader.loadAsync(this.dataset.modelUrl);

      this.#model = gltf.scene;
      this.tuneMaterials();
      this.frameModel(three);
      this.#scene.add(this.#model);
      this.#spin = this.travelProgress();

      this.#observer = new ResizeObserver(() => {
        this.resize();
        this.frameModel(three);
        this.render();
      });

      this.#observer.observe(this.elements.visual);

      if (this.elements.start_media) {
        this.#observer.observe(this.elements.start_media);
      }

      this.measure();
      this.settle();
    } catch (error) {
      this.#renderer = null;
      this.#model = null;
      this.settle();
      console.error("[product scroll story] 3D failed to load, falling back to the static card", error);
    }
  }

  build(three) {
    this.#renderer = new three.WebGLRenderer({
      canvas: this.elements.canvas,
      alpha: true,
      antialias: true
    });

    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.#renderer.toneMapping = three.NeutralToneMapping;
    this.#renderer.toneMappingExposure = 0.92;
    this.#renderer.outputColorSpace = three.SRGBColorSpace;

    this.#scene = new three.Scene();
    this.#scene.environment = this.buildEnvironment(three);
    this.#scene.environmentIntensity = 0.35;

    this.#camera = new three.PerspectiveCamera(35, 1, 0.1, 100);

    this.#scene.add(new three.AmbientLight(0xffffff, 0.6));

    const key = new three.DirectionalLight(0xffffff, 2.0);
    key.position.set(2, 3, 4);
    this.#scene.add(key);

    const fill = new three.DirectionalLight(0xffffff, 0.8);
    fill.position.set(-3, 1, -2);
    this.#scene.add(fill);

    this.resize();
  }

  buildEnvironment(three) {
    const studio = new three.Scene();

    studio.add(
      new three.Mesh(
        new three.BoxGeometry(12, 12, 12),
        new three.MeshBasicMaterial({ color: 0x0e0e11, side: three.BackSide })
      )
    );

    const panel = (width, height, intensity, position, rotation) => {
      const material = new three.MeshBasicMaterial();

      material.color.setScalar(intensity);

      const mesh = new three.Mesh(new three.PlaneGeometry(width, height), material);

      mesh.position.set(position[0], position[1], position[2]);
      mesh.rotation.set(rotation[0], rotation[1], rotation[2]);
      studio.add(mesh);
    };

    panel(4, 4, 11, [0, 5.6, 0], [Math.PI / 2, 0, 0]);
    panel(0.55, 9, 26, [-4.2, 0.4, 1.9], [0, Math.PI / 2, 0]);
    panel(0.4, 9, 16, [4.2, 0.4, 1.2], [0, -Math.PI / 2, 0]);
    panel(0.5, 8, 9, [-1.6, 0.4, 5.4], [0, Math.PI, 0]);
    panel(6, 6, 7, [0, -0.4, 5.6], [0, Math.PI, 0]);
    panel(8, 8, 4, [0, -5.6, 0], [-Math.PI / 2, 0, 0]);

    const generator = new three.PMREMGenerator(this.#renderer);
    const environment = generator.fromScene(studio, 0.005).texture;

    generator.dispose();
    studio.traverse((node) => {
      node.geometry?.dispose();
      node.material?.dispose();
    });

    return environment;
  }

  tuneMaterials() {
    this.#model.traverse((node) => {
      const material = node.material;

      if (!material || material.metalness === undefined) {
        return;
      }

      if (material.metalness < 0.5) {
        material.envMapIntensity = 1;
        return;
      }

      material.roughnessMap = null;
      material.metalnessMap = null;
      material.aoMap = null;
      material.roughness = 0.38;
      material.metalness = 1;
      material.envMapIntensity = 2.1;
      material.needsUpdate = true;
    });
  }

  frameModel(three) {
    const pose = this.#model.rotation.clone();

    this.#model.rotation.set(0, 0, 0);
    this.#model.position.z = this.#model.position.z - this.#lunge;
    this.#lunge = 0;

    if (this.#float_base !== null) {
      this.#model.position.y = this.#float_base;
      this.#model.position.x = this.#float_base_x;
    }

    const box = new three.Box3().setFromObject(this.#model);
    const size = box.getSize(new three.Vector3());
    const centre = box.getCenter(new three.Vector3());

    this.#model.position.sub(centre);
    this.#model.rotation.copy(pose);

    this.#float_base = this.#model.position.y;
    this.#float_base_x = this.#model.position.x;
    this.#float_span = size.y;

    const visual = this.elements.visual.offsetHeight;
    const photo = this.photoHeight() ?? visual * 0.37;
    const fraction = this.clamp(photo / visual, 0.15, 0.8);

    this.#can_height = fraction * visual;
    const distance = size.y / (2 * fraction * Math.tan((this.#camera.fov * Math.PI) / 360));

    this.#camera.position.set(0, 0, distance);
    this.#camera.lookAt(0, 0, 0);
  }

  resize() {
    const visual = this.elements.visual;

    if (!this.#renderer || !visual) {
      return;
    }

    const width = visual.offsetWidth;
    const height = visual.offsetHeight;

    this.#renderer.setSize(width, height, false);
    this.#camera.aspect = width / height;
    this.#camera.updateProjectionMatrix();
  }

  render() {
    if (!this.#renderer || !this.#model) {
      return;
    }

    this.poseModel();
    this.#renderer.render(this.#scene, this.#camera);
  }

  poseModel() {
    const offset = (parseFloat(this.dataset.modelRotation) || 0) * Math.PI / 180;

    const spin = this.#spin;
    const swing = Math.sin(spin * Math.PI);
    const eased = spin * spin * (3 - (2 * spin));
    const tumble = 1 - Math.pow(1 - spin, TUMBLE_LEAD);

    this.#model.rotation.y = offset + (eased * Math.PI * 2);
    this.#model.rotation.x = (tumble * Math.PI * 2) + (Math.sin(spin * Math.PI * 3) * TUMBLE_WOBBLE);
    this.#model.rotation.z = Math.sin((spin * Math.PI * 2) + LEAN_PHASE) * swing * MAX_LEAN;

    const carried = this.floatAmount();

    if (this.#float_base !== null) {
      const now = performance.now();
      const bob = Math.sin((now / FLOAT_PERIOD) * Math.PI * 2);
      const sway = Math.sin((now / FLOAT_SWAY_PERIOD) * Math.PI * 2);
      const pitch = Math.sin((now / FLOAT_PITCH_PERIOD) * Math.PI * 2);

      this.#model.position.y = this.#float_base + (bob * this.#float_span * FLOAT_RISE * carried);
      this.#model.position.x = this.#float_base_x + (sway * this.#float_span * FLOAT_SWAY * carried);
      this.#model.rotation.y = this.#model.rotation.y + (sway * FLOAT_TURN * carried);
      this.#model.rotation.x = this.#model.rotation.x + (pitch * FLOAT_PITCH * carried);
    }

    const lunge = this.lungeReach() * this.#camera.position.z * LUNGE_DEPTH;

    this.#model.position.z = this.#model.position.z + (lunge - this.#lunge);
    this.#lunge = lunge;
  }

  chaseSpin() {
    if (this.#spin_frame || !this.#renderer || !this.#model) {
      return;
    }

    const step = () => {
      const gap = this.#progress - this.#spin;

      if (Math.abs(gap) < SPIN_SETTLED) {
        this.#spin = this.#progress;
        this.#spin_frame = null;
        this.render();
        return;
      }

      this.#spin = this.#spin + (gap * SPIN_EASE);

      if (!this.#float_frame) {
        this.render();
      }

      this.#spin_frame = requestAnimationFrame(step);
    };

    this.#spin_frame = requestAnimationFrame(step);
  }

  floatAmount() {
    if (this.#float_base === null) {
      return 0;
    }

    const lifted = this.smoothstep(0, FLOAT_HOLD, this.#progress);
    const settling = 1 - this.smoothstep(1 - FLOAT_HOLD, 1, this.#progress);

    return lifted * settling;
  }

  floatModel() {
    if (this.#float_frame || !this.#renderer || !this.#model) {
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const step = () => {
      if (!this.#started || this.floatAmount() <= 0) {
        this.#float_frame = null;
        this.render();
        return;
      }

      this.render();
      this.#float_frame = requestAnimationFrame(step);
    };

    this.#float_frame = requestAnimationFrame(step);
  }

  stopFloat() {
    cancelAnimationFrame(this.#float_frame);
    this.#float_frame = null;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

customElements.define("component-scroll-story", ComponentScrollStory);
